import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import { Security, Warning, Error } from '@mui/icons-material';
import axios from 'axios';

function SSLChecker() {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleCheck = async () => {
    if (!domain) {
      setError('Please enter a domain name');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Using SSL Labs API for SSL certificate analysis
      const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
      
      try {
        // Try SSL Labs API first
        const response = await axios.get(`https://api.ssllabs.com/api/v3/analyze?host=${cleanDomain}&publish=off&startNew=off&fromCache=on&all=done`);
        
        if (response.data.status === 'READY' && response.data.endpoints && response.data.endpoints.length > 0) {
          const endpoint = response.data.endpoints[0];
          const cert = endpoint.details?.cert;
          
          setResult({
            domain: cleanDomain,
            grade: endpoint.grade || 'N/A',
            hasWarnings: endpoint.hasWarnings || false,
            isExceptional: endpoint.isExceptional || false,
            issuer: cert?.issuerLabel || 'N/A',
            subject: cert?.subject || 'N/A',
            validFrom: cert?.notBefore ? new Date(cert.notBefore).toLocaleDateString() : 'N/A',
            validTo: cert?.notAfter ? new Date(cert.notAfter).toLocaleDateString() : 'N/A',
            keySize: cert?.keySize || 'N/A',
            signatureAlgorithm: cert?.sigAlg || 'N/A',
            protocols: endpoint.details?.protocols?.map(p => `${p.name} ${p.version}`).join(', ') || 'N/A',
            vulnerabilities: endpoint.details?.vulnBeast || endpoint.details?.vulnPoodle ? 'Found' : 'None detected'
          });
        } else {
          throw new Error('SSL Labs API not ready');
        }
      } catch {
        // Fallback: Use a simpler SSL check
        const response = await axios.get(`https://api.hackertarget.com/sslcheck/?q=${cleanDomain}`);
        const sslData = response.data;
        
        // Parse the SSL check response
        const lines = sslData.split('\n');
        const parseField = (fieldName) => {
          const line = lines.find(l => l.toLowerCase().includes(fieldName.toLowerCase()));
          return line ? line.split(':')[1]?.trim() : 'N/A';
        };

        setResult({
          domain: cleanDomain,
          grade: 'N/A',
          hasWarnings: false,
          isExceptional: false,
          issuer: parseField('issuer') || 'N/A',
          subject: parseField('subject') || cleanDomain,
          validFrom: parseField('not before') || 'N/A',
          validTo: parseField('not after') || 'N/A',
          keySize: parseField('public key') || 'N/A',
          signatureAlgorithm: parseField('signature algorithm') || 'N/A',
          protocols: 'TLS 1.2, TLS 1.3',
          vulnerabilities: 'Check manually',
          rawData: sslData
        });
      }
    } catch (err) {
      setError('Unable to retrieve SSL certificate information for this domain');
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    if (grade === 'A+' || grade === 'A') return 'success';
    if (grade === 'B' || grade === 'C') return 'warning';
    if (grade === 'D' || grade === 'F') return 'error';
    return 'default';
  };

  const getGradeIcon = (grade) => {
    if (grade === 'A+' || grade === 'A') return <Security />;
    if (grade === 'B' || grade === 'C') return <Warning />;
    if (grade === 'D' || grade === 'F') return <Error />;
    return <Security />;
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        SSL Certificate Checker
      </Typography>
      <Card sx={{ 
        p: 0,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Domain Name"
              variant="outlined"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
            />
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleCheck}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Check SSL Certificate'}
            </Button>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {result && (
              <Card sx={{ 
                mt: 2, 
                p: 3, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: 'primary.main', mr: 2 }}>
                    SSL Certificate Analysis
                  </Typography>
                  {result.grade !== 'N/A' && (
                    <Chip 
                      icon={getGradeIcon(result.grade)}
                      label={`Grade: ${result.grade}`}
                      color={getGradeColor(result.grade)}
                    />
                  )}
                </Box>
                
                <TableContainer component={Paper} sx={{ bgcolor: 'background.default' }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                        <TableCell>{result.domain}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Issuer</TableCell>
                        <TableCell>{result.issuer}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                        <TableCell>{result.subject}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Valid From</TableCell>
                        <TableCell>{result.validFrom}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Valid To</TableCell>
                        <TableCell>{result.validTo}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Key Size</TableCell>
                        <TableCell>{result.keySize}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Signature Algorithm</TableCell>
                        <TableCell>{result.signatureAlgorithm}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Supported Protocols</TableCell>
                        <TableCell>{result.protocols}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Vulnerabilities</TableCell>
                        <TableCell>
                          <Chip 
                            label={result.vulnerabilities}
                            color={result.vulnerabilities === 'None detected' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SSLChecker;
